import { useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import cn from 'clsx'
import { Loader, Typography } from '@corecomponents/ui'
import useGraphAnalytics from '@api/hooks/ambassdor/useGraphAnalytics'
import {
  NOTIFICATION_TYPE,
  PROFILE_STAGE,
  PROGRAM_TYPE,
  TOOL_TYPE,
} from '@utils/enum'
import { Notification } from '@utils/utilities'
import { TILE_NO, TILE_TYPE } from '@utils/constants'
import LineChart from '../LineChart'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import s from './StatisticsData.module.css'
import {
  ASSITED_SALES_PAYOUT,
  ASSITED_SALES_REVENUE,
  ASSITED_SALES_SALE,
  CAP_INFO_ID,
  INVALID_RANGE,
  PAYOUT_CAP_INFO,
  PAYOUTS_TILES,
  PERSONAL_PAYOUT,
  PERSONAL_REVENUE,
  PERSONAL_SALES,
  REVENUE_TILES,
  SAATHI_PAYOUT_DATA,
  SAATHI_REVENUSE_DATA,
  SAATHI_SALES_DATA,
  SALES_TILES,
  SIGN_UP_EARNING,
  SIGN_UP_ORDER_TYPE,
  TOP,
  TOTAL_SIGN_UP,
} from '@utils/translation'
import { useAmbassadorContext } from '@context/context'
import useSaathiAnalytics from '@api/hooks/useSaathiAnalytics'

const StatisticsData = ({
  startDate,
  endDate,
  applyYearlyMetrics,
  cameFrom,
  orderType,
}: {
  startDate: string
  endDate: string
  applyYearlyMetrics: boolean
  cameFrom?: string
  orderType: number
}) => {
  const { myProfileValue } = useAmbassadorContext()
  const [graphApiData, setGraphApiData] = useState<any>({})
  const [saathiLeadsData, setSaathiLeadsData] = useState<any>({})

  const {
    data: graphiDetails,
    error: errorMsg,
    isFetching,
  } = useGraphAnalytics(graphApiData)

  const { data: saathiDetails, error: errMsg } =
    useSaathiAnalytics(saathiLeadsData)

  const handleRevenue = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi
      ? saathiDetails?.revenue || 0
      : graphiDetails?.totalRevenue || 0
  }

  const handlePayouts = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi
      ? saathiDetails?.payout || 0
      : graphiDetails?.totalPayouts || 0
  }

  const handleRevenueName = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi ? TILE_TYPE.Accepted : TILE_TYPE.Revenue
  }

  const handleConversionName = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi ? TILE_TYPE.Conversion : TILE_TYPE.Sales
  }

  const handleSuccessName = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi
      ? saathiDetails?.convertedLeadCount || 0
      : graphiDetails?.totalSales || 0
  }

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount).toLocaleString('en-IN')}`
  }

  const StatisticsDataType = [
    {
      name: handleRevenueName(),
      amount: formatCurrency(handleRevenue()),
    },
    {
      name: handleConversionName(),
      amount: handleSuccessName(),
    },
    {
      name: TILE_TYPE.Payouts,
      amount: formatCurrency(handlePayouts()),
    },
  ]
  const StatisticsDataTypeForSignUp = [
    {
      name: TILE_TYPE.earning,
      amount: graphiDetails?.totalPayouts || 0,
    },
    {
      name: TILE_TYPE.SignUp,
      amount: graphiDetails?.totalSales || 0,
    },
  ]

  const dotsColorClassName = (index: number) =>
    cn(s.tile_color_dots, {
      ['bg-[#EDB84F]']: index === 0 && orderType != SIGN_UP_ORDER_TYPE,
      ['bg-[#D2CCFF]']: index === 1,
      ['bg-[#2B91C9]']:
        index === 2 || (index === 0 && orderType === SIGN_UP_ORDER_TYPE),
    })

  useEffect(() => {
    if (myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM) {
      setGraphApiData({
        startDate: startDate,
        endDate: endDate,
        orderType: orderType,
        ...(applyYearlyMetrics && { applyYearlyMetrics }),
      })
    } else {
      setSaathiLeadsData({
        startDate: startDate,
        endDate: endDate,
        orgId: process.env.REACT_APP_PP_PW_ORGANIZATION_ID,
        type: 'SAATHI',
      })
    }
  }, [startDate, endDate, orderType])

  useEffect(() => {
    if (errorMsg) {
      const getErrorMessage = errorMsg.message || INVALID_RANGE
      Notification(getErrorMessage, NOTIFICATION_TYPE.ERROR)
    }
  }, [errorMsg])

  const filteredStats = () => {
    if (cameFrom === PROGRAM_TYPE.CSC_PROGRAM) {
      return StatisticsDataType.slice(0, 2)
    } else if (orderType === SIGN_UP_ORDER_TYPE) {
      return StatisticsDataTypeForSignUp
    } else {
      return StatisticsDataType
    }
  }

  const getToolInfo = (
    type: TOOL_TYPE.SALES | TOOL_TYPE.REVENUE | TOOL_TYPE.PAYOUT
  ) => {
    if (myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM) {
      return type === TOOL_TYPE.SALES
        ? SAATHI_SALES_DATA
        : type === TOOL_TYPE.REVENUE
        ? SAATHI_REVENUSE_DATA
        : SAATHI_PAYOUT_DATA
    }

    // Map orderType -> data for each type
    const typeMap: Record<number, Record<string, any>> = {
      [SIGN_UP_ORDER_TYPE]: {
        sales: TOTAL_SIGN_UP,
        revenue: SIGN_UP_EARNING,
        payout: PAYOUTS_TILES,
      },
      3: {
        sales: ASSITED_SALES_SALE,
        revenue: ASSITED_SALES_REVENUE,
        payout: ASSITED_SALES_PAYOUT,
      },
      6: {
        sales: PERSONAL_SALES,
        revenue: PERSONAL_REVENUE,
        payout: PERSONAL_PAYOUT,
      },
    }

    return (
      typeMap[orderType]?.[type] ??
      (type === TOOL_TYPE.SALES
        ? REVENUE_TILES
        : type === TOOL_TYPE.REVENUE
        ? SALES_TILES
        : PAYOUTS_TILES)
    )
  }

  return (
    <div>
      <div className={s.tile_section}>
        {filteredStats().map((items: any, index: number) => {
          return (
            <div
              className={s.tile_box}
              style={{ boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.08)' }}
            >
              <div className={s.tile_info}>
                <div className={s.tile_title}>
                  <div className={dotsColorClassName(index)}></div>
                  <Typography variant={'regular'} weight={500}>
                    <span className={s.tile_name}>{items.name}</span>
                  </Typography>
                </div>

                <div className={s.tile_info_div}>
                  <InformationCircleIcon
                    className={s.tile_info_circle}
                    id={
                      index === 0
                        ? TILE_NO.TILE_NO_1
                        : index === 1
                        ? TILE_NO.TILE_NO_2
                        : index === 2
                        ? TILE_NO.TILE_NO_3
                        : ''
                    }
                  />
                </div>
              </div>
              <div className={s.tile_amount_info}>
                <Typography weight={500}>
                  <span className={s.tile_amount}>{items.amount}</span>
                </Typography>

                {/* {index === 2 &&
                  myProfileValue?.programType !==
                    PROFILE_STAGE.SAATHI_PROGRAM && (
                    <InformationCircleIcon
                      id={CAP_INFO_ID}
                      height={24}
                      width={24}
                    />
                  )} */}
              </div>
            </div>
          )
        })}
      </div>
      {myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM && (
        <div className={s.graph_section}>
          <div className={s.inner_section}>
            {isFetching ? (
              <div className={s.graph_loader}>
                <Loader color={`#5a4bda`} scale={3} />
              </div>
            ) : (
              <LineChart
                graphiDetails={graphiDetails?.analytics}
                startDate={startDate}
                endDate={endDate}
                orderType={orderType}
              />
            )}
          </div>
        </div>
      )}
      <Tooltip
        anchorId={TILE_NO.TILE_NO_1}
        place={TOP}
        className={s.tool_tip}
        content={getToolInfo(TOOL_TYPE.REVENUE)}
      />
      <Tooltip
        anchorId={TILE_NO.TILE_NO_2}
        place={TOP}
        className={s.tool_tip}
        content={getToolInfo(TOOL_TYPE.SALES)}
      />
      <Tooltip
        anchorId={TILE_NO.TILE_NO_3}
        place={TOP}
        className={s.tool_tip}
        content={getToolInfo(TOOL_TYPE.PAYOUT)}
      />
      <Tooltip
        anchorId={CAP_INFO_ID}
        place={TOP}
        className={s.tool_tip}
        content={PAYOUT_CAP_INFO}
      />
    </div>
  )
}

export default StatisticsData
